import { supabase } from '@/lib/supabase';
import type { DataTableCell, DataTableCellTone, DataTableColumn } from '@shared/types/views';
import type { AiSegment, ReviewCriterion } from '@/lib/app-reviews';
import type { AppObjectKind } from '@shared/types/app-objects';

/**
 * F5a: the app's own list. Each completed run merges into it (rows of a list
 * app, or one object per run); people's status, assignee, due date and archive
 * are written by RPCs with a version check, and every change is in the history.
 */
export interface AppStatus {
  key: string;
  label: string;
  tone: DataTableCellTone;
  done?: boolean;
}

export interface AppCollection {
  id: string;
  title: string;
  mode: 'rows' | 'runs';
  columns: DataTableColumn[];
  statuses: AppStatus[];
  /** F5c: the human review grid, null when the app has none. */
  reviewGrid: ReviewCriterion[] | null;
  /** F5e: what the last run brought, including the rows it could not keep. */
  lastMerge: { runId: string; merged: number; skipped: number; issue: 'identity_missing' | 'no_table' | null } | null;
  /** L48: what an object is; a company or a person gets a badge with its initials (U2). */
  objectKind?: AppObjectKind | null;
}

export interface AppRecord {
  id: string;
  recordKey: string;
  computed: DataTableCell[];
  status: string;
  statusChangedBy: string | null;
  statusChangedAt: string | null;
  assigneeUserId: string | null;
  dueDate: string | null;
  archivedAt: string | null;
  firstRunId: string | null;
  lastRunId: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  version: number;
  /** F5c: the person whose run it is (per-run apps). */
  ownerUserId: string | null;
  /** F5c: the AI's grid of the run, copied into the shared object. */
  aiGrid: AiSegment[] | null;
  /** F6: what Domino computed, before people's corrections (`computed` shows the corrected values). */
  computedByDomino: DataTableCell[];
  /** F6: corrected values, by cell key. */
  edits: Record<string, AppCellEdit>;
}

/** F6: a value corrected by a person (by hand or from the chat), kept apart from Domino's. */
export interface AppCellEdit {
  value: string | number;
  by: string | null;
  at: string;
  source: 'manual' | 'chat';
  reason: string | null;
}

export type AppRecordEventKind =
  | 'created' | 'updated_by_run' | 'status_changed' | 'assigned' | 'due_date_changed' | 'archived' | 'restored' | 'undone'
  | 'labeled' | 'unlabeled' | 'commented' | 'reviewed' | 'cell_edited' | 'attached';

export interface AppRecordEvent {
  id: string;
  kind: AppRecordEventKind;
  actorUserId: string | null;
  field: string | null;
  before: unknown;
  after: unknown;
  runId: string | null;
  undoesEventId: string | null;
  createdAt: string;
}

export const UNDOABLE_EVENTS: ReadonlySet<AppRecordEventKind> = new Set(['status_changed', 'assigned', 'due_date_changed', 'archived', 'restored', 'cell_edited']);

export interface AppMember {
  userId: string;
  name: string;
}

type RecordRow = {
  id: string; record_key: string; computed: DataTableCell[]; status: string; status_changed_by: string | null;
  status_changed_at: string | null; assignee_user_id: string | null; due_date: string | null; archived_at: string | null;
  first_run_id: string | null; last_run_id: string | null; first_seen_at: string; last_seen_at: string; version: number;
  owner_user_id: string | null; ai_grid: AiSegment[] | null; edits?: Record<string, AppCellEdit> | null;
};

/** Domino's cells with people's corrections on top: what every screen and figure reads. */
export const effectiveCells = (computed: DataTableCell[], edits: Record<string, AppCellEdit>): DataTableCell[] =>
  computed.map((cell) => edits[cell.key] ? { ...cell, value: edits[cell.key]!.value, tone: undefined } : cell)
    .concat(Object.entries(edits).filter(([key]) => !computed.some((cell) => cell.key === key)).map(([key, edit]) => ({ key, value: edit.value })));

export const toAppRecord = (row: RecordRow): AppRecord => ({
  id: row.id, recordKey: row.record_key, computed: effectiveCells(row.computed ?? [], row.edits ?? {}),
  computedByDomino: row.computed ?? [], edits: row.edits ?? {}, status: row.status,
  statusChangedBy: row.status_changed_by, statusChangedAt: row.status_changed_at, assigneeUserId: row.assignee_user_id,
  dueDate: row.due_date, archivedAt: row.archived_at, firstRunId: row.first_run_id, lastRunId: row.last_run_id,
  firstSeenAt: row.first_seen_at, lastSeenAt: row.last_seen_at, version: row.version, ownerUserId: row.owner_user_id, aiGrid: row.ai_grid ?? null,
});

/** The app's list and its records, or null when no run has filled it yet. */
export const loadAppList = async (appId: string): Promise<{ collection: AppCollection; records: AppRecord[] } | null> => {
  const { data: collection, error } = await supabase.from('app_collections')
    .select('id, title, mode, columns, statuses, review_grid, last_merge, object_kind').eq('tool_app_id', appId).eq('key', 'main').maybeSingle();
  if (error) throw new Error('Impossible de charger la liste de cette application.');
  if (!collection) return null;
  const { data: rows, error: recordsError } = await supabase.from('app_records').select('*')
    .eq('collection_id', collection.id).order('last_seen_at', { ascending: false });
  if (recordsError) throw new Error('Impossible de charger la liste de cette application.');
  return {
    collection: { ...collection, reviewGrid: (collection.review_grid as ReviewCriterion[] | null) ?? null,
      lastMerge: (collection.last_merge as AppCollection['lastMerge']) ?? null,
      objectKind: (collection.object_kind as AppObjectKind | null) ?? null } as AppCollection,
    records: ((rows ?? []) as RecordRow[]).map(toAppRecord),
  };
};

export const loadAppMembers = async (): Promise<AppMember[]> => {
  const { data, error } = await supabase.from('tenant_members').select('user_id, display_name').order('display_name');
  if (error) throw new Error('Impossible de charger les membres de l’équipe.');
  return (data ?? []).map((member) => ({
    userId: member.user_id as string,
    name: (member.display_name as string | null)?.trim() || 'Membre sans nom affiché',
  }));
};

export const loadRecordEvents = async (recordId: string): Promise<AppRecordEvent[]> => {
  const { data, error } = await supabase.from('app_record_events').select('*')
    .eq('record_id', recordId).order('created_at', { ascending: false });
  if (error) throw new Error('Impossible de charger l’historique.');
  return (data ?? []).map((row) => ({
    id: row.id, kind: row.kind, actorUserId: row.actor_user_id, field: row.field, before: row.before, after: row.after,
    runId: row.run_id, undoesEventId: row.undoes_event_id, createdAt: row.created_at,
  }));
};

const RECORD_ERRORS: Record<string, string> = {
  app_record_version_conflict: 'Quelqu’un vient de modifier cette ligne : elle a été rechargée, refaites votre changement.',
  app_record_changed_since: 'Cette valeur a changé depuis : l’annulation n’est plus possible.',
  app_record_event_already_undone: 'Ce changement a déjà été annulé.',
  app_record_assignee_invalid: 'Cette personne n’a pas accès à l’application.',
  app_record_archive_forbidden: 'Seuls le créateur de l’application et les managers archivent une ligne.',
  app_record_forbidden: 'Vous n’avez pas accès à cette application.',
  app_record_attach_forbidden: 'Seuls le créateur de l’application et les managers rattachent une ligne.',
  app_record_attach_two_crm_ids: 'Ces deux lignes ont des ID CRM différents : ce sont deux entreprises.',
  app_record_attach_other_list: 'Une ligne se rattache à une ligne de la même liste.',
  app_record_attach_same: 'Choisissez une autre ligne.',
};

const call = async (fn: string, args: Record<string, unknown>): Promise<AppRecord> => {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw new Error(RECORD_ERRORS[error.message] ?? 'La modification n’a pas pu être enregistrée. Réessayez.');
  return toAppRecord(data as RecordRow);
};

export interface AppRecordChanges {
  status?: string;
  assigneeUserId?: string | null;
  dueDate?: string | null;
}

export const updateAppRecord = (record: AppRecord, changes: AppRecordChanges) =>
  call('update_app_record_f5a', { p_record_id: record.id, p_expected_version: record.version, p_changes: changes });

export const archiveAppRecord = (record: AppRecord, archived: boolean) =>
  call('archive_app_record_f5a', { p_record_id: record.id, p_expected_version: record.version, p_archived: archived });

export const undoAppRecordEvent = (eventId: string) => call('undo_app_record_event_f5a', { p_event_id: eventId });

/** R4 (L48): everything of this object passes to another of the same list, then it disappears; returns the kept object. */
export const attachAppRecord = (record: AppRecord, intoRecordId: string) =>
  call('attach_app_record_r4', { p_record_id: record.id, p_expected_version: record.version, p_into_record_id: intoRecordId });

/** F6: the same correction by the object's id and the version the chat saw (a stale version is refused). */
export const editAppRecordCellsById = (recordId: string, expectedVersion: number, cells: Record<string, string | number | null>, source: 'manual' | 'chat', reason: string | null) =>
  call('edit_app_record_cells_f6', { p_record_id: recordId, p_expected_version: expectedVersion, p_cells: cells, p_source: source, p_reason: reason });
